/**
 * React Query Hooks for Trading Positions
 *
 * Provides hooks for fetching trading positions with automatic
 * caching and retry logic.
 */

import { useQuery } from '@tanstack/react-query';
import { positionAPI } from '../../services/tradingBotAPI';
import { queryKeys, staleTimeConfig } from '../../config/queryClient';
import type { TradingPosition } from '../../types';

/**
 * Fetch all positions
 *
 * @param strategyId - Optional strategy ID to filter positions
 * @returns Query with positions list
 */
export function usePositions(strategyId?: string) {
  return useQuery({
    queryKey: queryKeys.positions.list(strategyId),
    queryFn: async () => {
      const response = await positionAPI.getAll(strategyId);
      return response.data.data as TradingPosition[];
    },
    staleTime: staleTimeConfig.realtime, // 30 seconds for real-time positions
  });
}

/**
 * Fetch single position by ID
 *
 * @param id - Position ID
 * @returns Query with position details
 */
export function usePosition(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.positions.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Position ID is required');
      const response = await positionAPI.getById(id);
      return response.data.data as TradingPosition;
    },
    enabled: !!id, // Only fetch if id is provided
    staleTime: staleTimeConfig.realtime,
  });
}
